import { Link, useLocation } from 'react-router-dom';
import { Home, MessageCircle, User, Compass } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';

export function MobileNavigation() {
  const location = useLocation();
  const { user } = useAuth();
  const { getTotalUnreadCount } = useChats();
  const [username, setUsername] = useState<string>('');
  
  const unreadCount = getTotalUnreadCount();

  useEffect(() => {
    const fetchUsername = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      
      if (data?.username) {
        setUsername(data.username);
      }
    };
    
    fetchUsername();
  }, [user]);

  if (!user) return null;

  const navItems = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/discover', label: 'Discover', icon: Compass },
    { 
      href: '/inbox', 
      label: 'Messages', 
      icon: MessageCircle,
      badge: unreadCount > 0 ? (unreadCount >= 9 ? '9+' : unreadCount.toString()) : undefined
    },
    { href: username ? `/u/${username}` : '/profile', label: 'Profile', icon: User },
  ];

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t shadow-soft">
      <div className="flex items-center justify-around py-2 px-4">
        {navItems.map(({ href, label, icon: Icon, badge }) => {
          const isActive = location.pathname === href;
          
          return (
            <Link
              key={href}
              to={href}
              className={cn(
                "relative flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors",
                "min-h-[48px] min-w-[48px]", // Ensure touch-friendly targets
                isActive 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">{label}</span>
              
              {badge && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-5 min-w-5 text-xs px-1 flex items-center justify-center"
                >
                  {badge}
                </Badge>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}